import React from "react";
import { ArrowRightIcon, PlusIcon } from "@icons";
import {
    Link,
    type LinkForcedState,
    type LinkSize,
} from "@design-system/Link/Link";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./LinkPlayground.module.scss";

interface MatrixStateProps {
    disabled?: boolean;
    "data-forced-state"?: LinkForcedState;
}

const matrixStates: ReadonlyArray<{
    id: string;
    label: string;
    props: MatrixStateProps;
}> = [
    { id: "normal", label: "Normal", props: {} },
    { id: "hover", label: "Hover", props: { "data-forced-state": "hover" } },
    {
        id: "pressed",
        label: "Pressed",
        props: { "data-forced-state": "pressed" },
    },
    { id: "focus", label: "Focus", props: { "data-forced-state": "focus" } },
    { id: "disabled", label: "Disabled", props: { disabled: true } },
];

const matrixColumns: ReadonlyArray<{
    id: string;
    label: string;
    renderLink: (
        size: LinkSize,
        stateProps: MatrixStateProps,
    ) => React.ReactNode;
}> = [
    {
        id: "text",
        label: "Text",
        renderLink: (size, stateProps) => (
            <Link size={size} {...stateProps}>
                Reports
            </Link>
        ),
    },
    {
        id: "leftIcon",
        label: "Left icon",
        renderLink: (size, stateProps) => (
            <Link size={size} leftIcon={<PlusIcon />} {...stateProps}>
                Reports
            </Link>
        ),
    },
    {
        id: "rightIcon",
        label: "Right icon",
        renderLink: (size, stateProps) => (
            <Link size={size} rightIcon={<ArrowRightIcon />} {...stateProps}>
                Reports
            </Link>
        ),
    },
    {
        id: "iconOnly",
        label: "Icon only",
        renderLink: (size, stateProps) => (
            <Link size={size} iconOnly aria-label="Next page" {...stateProps}>
                <ArrowRightIcon />
            </Link>
        ),
    },
];

const matrixSizes: readonly LinkSize[] = ["sm", "xs"];

/**
 * @purpose Figma's Link state sheet (4031:26), rendered live: content columns
 * against the five states, once per size. Hover, pressed and focus are forced
 * via `data-forced-state`; tab to a link to compare against the real ring.
 */
export const LinkMatrix = () => (
    <VStack className={styles.matrix} gap={24} padding={24}>
        <h2 className={styles.matrixTitle}>Size × content × state</h2>
        {matrixSizes.map((size) => (
            <VStack key={size} gap={8}>
                <h3 className={styles.groupTitle}>{size}</h3>
                <div className={styles.grid}>
                    <span />
                    {matrixColumns.map((column) => (
                        <span key={column.id} className={styles.columnHeader}>
                            {column.label}
                        </span>
                    ))}
                    {matrixStates.map((state) => (
                        <React.Fragment key={state.id}>
                            <span className={styles.rowLabel}>
                                {state.label}
                            </span>
                            {matrixColumns.map((column) => (
                                <div key={column.id} className={styles.cell}>
                                    {column.renderLink(size, state.props)}
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </VStack>
        ))}
    </VStack>
);
